import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { IActivity, KeyStage, FractionWallConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import {
    StageCard,
    JuicyButton,
    TrafficLightIndicator,
    TrafficLightStatus,
} from './primitives';
import { AudioPromptButton } from './AudioPromptButton';
import { Scale, Plus, RotateCcw } from 'lucide-react-native';

interface FractionWallActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

const DENOM_COLORS: Record<number, string> = {
    1: '#64748B',
    2: '#EF4444',
    3: '#F97316',
    4: '#EAB308',
    5: '#10B981',
    6: '#06B6D4',
    8: '#3B82F6',
    10: '#8B5CF6',
    12: '#EC4899',
};

export const FractionWallActivity: React.FC<FractionWallActivityProps> = ({
    activity,
    onComplete,
    keyStage: _keyStage,
}) => {
    const config = (activity.config || {}) as FractionWallConfig;
    const target = config.targetFraction || { numerator: 1, denominator: 2 };
    const availableDenominators = useMemo(() => config.availableDenominators || [2, 3, 4, 6, 8, 12], [config.availableDenominators]);

    // Active placed fraction tiles: array of denominators
    const [placedTiles, setPlacedTiles] = useState<number[]>([]);
    const [trafficStatus, setTrafficStatus] = useState<TrafficLightStatus>('idle');
    const [statusText, setStatusText] = useState<string>(
        config.prompt || `Build a fraction equivalent to ${target.numerator}/${target.denominator} using different tiles.`
    );
    const [hasCompleted, setHasCompleted] = useState(false);

    // Target value as floating point (e.g. 1/2 = 0.5)
    const targetVal = target.numerator / target.denominator;

    // Current sum of placed tiles
    const currentSum = useMemo(() => {
        return placedTiles.reduce((acc, d) => acc + 1 / d, 0);
    }, [placedTiles]);

    const isMatch = Math.abs(currentSum - targetVal) < 0.0001;
    const isOverflow = currentSum > targetVal + 0.0001;

    const addTile = (denom: number) => {
        if (hasCompleted) return;
        setPlacedTiles((prev) => [...prev, denom]);
        setTrafficStatus('idle');
    };

    const removeTile = (index: number) => {
        if (hasCompleted) return;
        setPlacedTiles((prev) => prev.filter((_, i) => i !== index));
        setTrafficStatus('idle');
    };

    const clearTiles = () => {
        if (hasCompleted) return;
        setPlacedTiles([]);
        setTrafficStatus('idle');
    };

    const handleCheck = () => {
        if (placedTiles.length === 0 || hasCompleted) return;

        // Ensure student didn't just use the exact same single tile (e.g., if target is 1/2, picking 1/2 alone is trivial)
        const isExactIdentical =
            placedTiles.length === target.numerator && placedTiles.every((d) => d === target.denominator);

        if (isMatch && !isExactIdentical) {
            setTrafficStatus('correct');
            setStatusText(`Equivalence Found! Your tiles equal exactly ${target.numerator}/${target.denominator}.`);
            setHasCompleted(true);

            useStrokeStore.getState().addCustomAnswer({
                activityId: activity.id,
                value: placedTiles,
                mechanic: 'fraction_wall',
                isCorrect: true,
                score: 100,
            });

            setTimeout(() => {
                onComplete();
            }, 1200);
        } else if (isExactIdentical) {
            setTrafficStatus('incorrect');
            setStatusText(`Try using different fraction tiles to prove equivalence!`);
        } else if (isOverflow) {
            setTrafficStatus('incorrect');
            setStatusText(`Too much! Your tiles exceed ${target.numerator}/${target.denominator}. Remove some tiles.`);
        } else {
            setTrafficStatus('incorrect');
            setStatusText(`Not quite full. Keep adding tiles until they line up exactly with ${target.numerator}/${target.denominator}.`);
        }
    };

    return (
        <StageCard testID="fraction-wall-card">
            <View style={styles.container}>
                {/* Header */}
                <View style={styles.headerRow}>
                    <View style={styles.titleArea}>
                        <View style={styles.badge}>
                            <Scale size={14} color="#4F46E5" />
                            <Text style={styles.badgeText}>Fraction Wall & Equivalence</Text>
                        </View>
                        <Text style={styles.promptText}>{config.prompt || 'Match the target fraction bar.'}</Text>
                    </View>
                    <View style={styles.headerControls}>
                        {config.audioPromptText && (
                            <AudioPromptButton
                                promptText={config.audioPromptText}
                                activityId={activity.id}
                                testID="fraction-wall-audio-button"
                            />
                        )}
                        <TrafficLightIndicator status={trafficStatus} size={28} />
                    </View>
                </View>

                {/* Target Fraction Bar */}
                <View style={styles.targetSection}>
                    <View style={styles.targetLabelRow}>
                        <Text style={styles.targetLabel}>TARGET FRACTION:</Text>
                        <View style={styles.fractionPill}>
                            <Text style={styles.fractionPillText}>
                                {target.numerator}/{target.denominator}
                            </Text>
                        </View>
                    </View>
                    <View style={styles.targetTrack}>
                        <View
                            style={[
                                styles.targetBar,
                                {
                                    width: `${Math.min(100, Math.round(targetVal * 100))}%`,
                                    backgroundColor: DENOM_COLORS[target.denominator] || '#3B82F6',
                                },
                            ]}
                        >
                            <Text style={styles.targetBarText}>
                                {target.numerator}/{target.denominator}
                            </Text>
                        </View>
                    </View>
                </View>

                {/* Staged Working Drop Row */}
                <View style={styles.stageSection}>
                    <View style={styles.stageHeader}>
                        <Text style={styles.stageLabel}>YOUR FRACTION TILES ({placedTiles.length}):</Text>
                        <TouchableOpacity
                            style={styles.clearButton}
                            onPress={clearTiles}
                            disabled={hasCompleted || placedTiles.length === 0}
                            accessibilityLabel="Clear tiles"
                            testID="fraction-wall-clear"
                        >
                            <RotateCcw size={14} color="#64748B" />
                            <Text style={styles.clearButtonText}>Clear</Text>
                        </TouchableOpacity>
                    </View>

                    <View style={styles.stageTrack}>
                        {placedTiles.length === 0 ? (
                            <View style={styles.emptyStage}>
                                <Text style={styles.emptyStageText}>Tap tiles below to build equivalent length.</Text>
                            </View>
                        ) : (
                            <View style={styles.tilesRow}>
                                {placedTiles.map((denom, idx) => {
                                    const widthPct = (1 / denom) * 100;
                                    return (
                                        <TouchableOpacity
                                            key={`placed_${idx}_${denom}`}
                                            style={[
                                                styles.placedTile,
                                                {
                                                    width: `${widthPct}%`,
                                                    backgroundColor: DENOM_COLORS[denom] || '#64748B',
                                                },
                                            ]}
                                            onPress={() => removeTile(idx)}
                                            disabled={hasCompleted}
                                            accessibilityLabel={`Remove 1/${denom}`}
                                        >
                                            <Text style={styles.placedTileText}>1/{denom}</Text>
                                        </TouchableOpacity>
                                    );
                                })}
                            </View>
                        )}
                    </View>
                </View>

                {/* Feedback Status */}
                <View style={[styles.statusBox, isMatch && styles.statusBoxMatch, isOverflow && styles.statusBoxOverflow]}>
                    <Text style={[styles.statusBoxText, isMatch && styles.statusBoxTextMatch]}>{statusText}</Text>
                </View>

                {/* Tile Palette & Submit Button */}
                <View style={styles.paletteSection}>
                    <Text style={styles.paletteTitle}>Available Fraction Tiles:</Text>
                    <View style={styles.tilesPalette}>
                        {availableDenominators.map((d) => (
                            <TouchableOpacity
                                key={`palette_${d}`}
                                style={[styles.tileChoiceButton, { backgroundColor: DENOM_COLORS[d] || '#3B82F6' }]}
                                onPress={() => addTile(d)}
                                disabled={hasCompleted}
                                accessibilityLabel={`Add 1/${d}`}
                                testID={`add-tile-${d}`}
                            >
                                <Plus size={14} color="#FFFFFF" />
                                <Text style={styles.tileChoiceText}>1/{d}</Text>
                            </TouchableOpacity>
                        ))}
                    </View>

                    <View style={styles.actionRow}>
                        <JuicyButton
                            label="CHECK EQUIVALENCE"
                            variant="primary"
                            size="medium"
                            onPress={handleCheck}
                            disabled={hasCompleted || placedTiles.length === 0}
                            testID="fraction-wall-check-button"
                        />
                    </View>
                </View>
            </View>
        </StageCard>
    );
};

const styles = StyleSheet.create({
    container: {
        padding: 16,
        gap: 12,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        paddingBottom: 10,
    },
    titleArea: {
        flex: 1,
        paddingRight: 12,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        alignSelf: 'flex-start',
        marginBottom: 6,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#4F46E5',
    },
    promptText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#1E293B',
    },
    headerControls: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    targetSection: {
        backgroundColor: '#F8FAFC',
        padding: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 8,
    },
    targetLabelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    targetLabel: {
        fontSize: 12,
        fontWeight: '800',
        color: '#64748B',
    },
    fractionPill: {
        backgroundColor: '#1E293B',
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 8,
    },
    fractionPillText: {
        fontSize: 13,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    targetTrack: {
        height: 38,
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        overflow: 'hidden',
    },
    targetBar: {
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: 6,
    },
    targetBarText: {
        fontSize: 14,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    stageSection: {
        backgroundColor: '#F8FAFC',
        padding: 12,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: '#E2E8F0',
        gap: 8,
    },
    stageHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    stageLabel: {
        fontSize: 12,
        fontWeight: '800',
        color: '#64748B',
    },
    clearButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 2,
    },
    clearButtonText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
    },
    stageTrack: {
        height: 48,
        backgroundColor: '#FFFFFF',
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#CBD5E1',
        overflow: 'hidden',
        justifyContent: 'center',
    },
    emptyStage: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    emptyStageText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#94A3B8',
    },
    tilesRow: {
        flexDirection: 'row',
        height: '100%',
    },
    placedTile: {
        height: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        borderRightWidth: 1,
        borderRightColor: '#FFFFFF',
    },
    placedTileText: {
        fontSize: 13,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    statusBox: {
        paddingVertical: 8,
        paddingHorizontal: 12,
        backgroundColor: '#F1F5F9',
        borderRadius: 8,
    },
    statusBoxMatch: {
        backgroundColor: '#DCFCE7',
    },
    statusBoxOverflow: {
        backgroundColor: '#FEE2E2',
    },
    statusBoxText: {
        fontSize: 13,
        fontWeight: '600',
        color: '#475569',
        textAlign: 'center',
    },
    statusBoxTextMatch: {
        color: '#15803D',
        fontWeight: '800',
    },
    paletteSection: {
        gap: 8,
        alignItems: 'center',
    },
    paletteTitle: {
        fontSize: 13,
        fontWeight: '800',
        color: '#475569',
        alignSelf: 'flex-start',
    },
    tilesPalette: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
    },
    tileChoiceButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 10,
        minWidth: 64,
        justifyContent: 'center',
    },
    tileChoiceText: {
        fontSize: 14,
        fontWeight: '900',
        color: '#FFFFFF',
    },
    actionRow: {
        marginTop: 6,
    },
});
